import { useRouter } from 'next/router';
import { memo, useMemo } from 'react';

const ITEM_PER_BLOCK = 3;
const COUNT_PER_SIDE = Math.floor(ITEM_PER_BLOCK / 2);

interface Props {
  totalPage: number;
  currentPage: number;
  onChangePage: (page: number) => void;
}

const Pagination = memo(({ totalPage, currentPage, onChangePage }: Props) => {
  const countRightSide =
    currentPage < ITEM_PER_BLOCK - COUNT_PER_SIDE
      ? ITEM_PER_BLOCK - currentPage
      : currentPage > totalPage - COUNT_PER_SIDE
      ? totalPage - currentPage
      : COUNT_PER_SIDE;
  const countLeftSide = ITEM_PER_BLOCK - countRightSide - 1;
  const listPaginationAvailable = useMemo(
    () =>
      totalPage <= ITEM_PER_BLOCK
        ? [...new Array(totalPage).fill(0).map((_, index) => index + 1)]
        : [
            ...new Array(countLeftSide)
              .fill(0)
              .map((_, index) => currentPage - (index + 1)),
            currentPage,
            ...new Array(countRightSide)
              .fill(0)
              .map((_, index) => currentPage + (index + 1)),
          ].sort((a, b) => a - b),
    [countLeftSide, countRightSide, currentPage, totalPage],
  );

  const isShowJumpLeft = listPaginationAvailable[0] - 1 > 1;
  const isShowJumpRight =
    listPaginationAvailable[listPaginationAvailable.length - 1] + 1 < totalPage;

  const onSetCurrentPage = (event, page) => {
    event.preventDefault();
    onChangePage(page);
  };

  const onPrevPage = event => {
    event.preventDefault();
    if (currentPage - 1 > 0) onSetCurrentPage(event, currentPage - 1);
  };

  const onNextPage = event => {
    event.preventDefault();
    if (currentPage + 1 < totalPage + 1)
      onSetCurrentPage(event, currentPage + 1);
  };
  const router = useRouter();
  const pageUrl = router.asPath.split('?')[0];
  return (
    !!totalPage && (
      <nav aria-label="Page navigation example">
        <ul className="pagination justify-content-center mb-0 flex-wrap">
          {currentPage - 1 >= 1 && (
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <a
                href={`${pageUrl}${
                  currentPage - 1 <= 1 ? '' : `?page=${currentPage - 1}`
                }`}
                className="page-link --link"
                onClick={onPrevPage}
              >
                <i className="fa-solid fa-angles-left" />
              </a>
            </li>
          )}

          {!listPaginationAvailable.includes(1) && (
            <li className="page-item">
              <a
                href={`${pageUrl}`}
                className="--link page-link"
                onClick={event => onSetCurrentPage(event, 1)}
              >
                1
              </a>
            </li>
          )}
          {isShowJumpLeft && (
            <li className="page-item">
              <span className="page-link --link">...</span>
            </li>
          )}
          {listPaginationAvailable?.map((item, index) => (
            <li
              key={index}
              className="page-item"
              onClick={event => onSetCurrentPage(event, item)}
            >
              <a
                href={item === 1 ? `${pageUrl}` : `${pageUrl}?page=${item}`}
                className={`--link page-link ${
                  currentPage === item ? 'active' : ''
                }`}
              >
                {item}
              </a>
            </li>
          ))}
          {isShowJumpRight && (
            <li className="page-item">
              <span className="page-link --link">...</span>
            </li>
          )}
          {!listPaginationAvailable.includes(totalPage) && (
            <li className="page-item">
              <a
                href={`${pageUrl}?page=${totalPage}`}
                className="--link page-link"
                onClick={event => onSetCurrentPage(event, totalPage)}
              >
                {totalPage}
              </a>
            </li>
          )}
          {currentPage + 1 <= totalPage && (
            <li
              className={`page-item ${
                currentPage === totalPage ? 'disabled' : ''
              }`}
            >
              <a
                href={`${pageUrl}?page=${currentPage + 1}`}
                className="page-link --link"
                onClick={onNextPage}
              >
                <i className="fa-solid fa-angles-right" />
              </a>
            </li>
          )}
        </ul>
      </nav>
    )
  );
});

export default Pagination;
